import Mustache from 'mustache';

/**
 * Site search UI module.
 *
 * @class SiteSearch
 * @param {HTMLElement}    parent               - Outermost container element.
 */

class SiteSearch {
  constructor(parent) {
    this.parent = parent;
    this.form = parent.getElementsByClassName('site-search__form')[0];
    this.openButton = parent.getElementsByClassName('site-search__open-button')[0];
    this.closeButton = parent.getElementsByClassName('site-search__close-button')[0];
    this.submitButton = parent.getElementsByClassName('site-search__submit-button')[0];
    this.output = parent.getElementsByClassName('site-search__output')[0];
    this.resultsWrapper = parent.getElementsByClassName('site-search__results-wrapper')[0];
    this.spinner = parent.getElementsByClassName('site-search__spinner')[0];
    this.mainNav = document.getElementsByClassName('site-header')[0];

    this.ajaxUrl = new URL('/stage-ajax/getSearchResults', window.location.origin);
    this.searchParams = new URLSearchParams({ 'stage-language': document.documentElement.lang || 'fi' });

    this.form.elements.search.addEventListener('input', event => this.onSearchInput(event.target.value));
    this.form.elements.search.addEventListener('keydown', event => this.onSearchKeydown(event.key));
    this.form.addEventListener('submit', event => this.onFormSubmit(event));
    this.openButton.addEventListener('click', () => this.openForm());
    this.closeButton.addEventListener('click', () => this.onCloseButtonClick());
    document.addEventListener('click', event => this.onDocumentClick(event.target));
  }

  /**
   * Dispatch form submit with debounce on search input.
   *
   * @param  {string} term - Search term.
   *
   * @return {undefined}
   */

  onSearchInput(term) {
    this.inputTimer && clearTimeout(this.inputTimer);
    term = term.trim();
    this.searchParams.set('search', term);

    this.inputTimer = setTimeout(() => {
      if (term) {
        if (this.closeButton.hidden) {
          this.submitButton.hidden = true;
          this.closeButton.hidden = false;
        }

        this.form.dispatchEvent(new Event('submit'));
      }
      else {
        if (!this.closeButton.hidden) {
          this.closeButton.hidden = true;
          this.submitButton.hidden = false;
        }

        this.parent.getElementsByClassName('site-search__results')[0]?.remove();
      }
    }, 500);
  }

  /**
   * Close form on search ESC keydown.
   *
   * @param  {string} key - Pressed key.
   *
   * @return {undefined}
   */

  onSearchKeydown(key) {
    if (key === 'Escape' || key === 'Esc') {
      this.closeForm();
    }
  }

  /**
   * Open search form.
   *
   * @return {undefined}
   */

  openForm() {
    this.form.hidden = false;
    this.output.hidden = false;
    this.form.elements.search.focus();
    document.body.classList.add('overflow-hidden');
    this.mainNav.classList.add('hide-links');

    if(this.resultsWrapper.innerHTML) {
      this.mainNav.classList.add('blue-background');
    }
    //overflow-hidden
  }

  /**
   * Close search form.
   *
   * @return {undefined}
   */

  closeForm() {
    this.inputTimer && clearTimeout(this.inputTimer);
    this.form.hidden = true;
    this.output.hidden = true;
    document.body.classList.remove('overflow-hidden');
    this.mainNav.classList.remove('hide-links');
    this.mainNav.classList.remove('blue-background');
  }

  /**
   * Handle close button click.
   *
   * @return {undefined}
   */

  onCloseButtonClick() {
    this.closeForm();
    this.openButton.focus();
  }

  /**
   * Fetch search results and update UI on form submit.
   *
   * @param  {Event}     event - Form submit event.
   *
   * @return {undefined}
   */

  async onFormSubmit(event) {
    this.inputTimer && clearTimeout(this.inputTimer);
    const term = this.searchParams.get('search');

    if (event.isTrusted && term) {
      return;
    }

    event.preventDefault();

    if (!term) {
      return;
    }

    this.ajaxUrl.search = this.searchParams.toString();
    this.parent.getElementsByClassName('site-search__results')[0]?.remove();
    this.spinner.hidden = false;
    this.mainNav.classList.add('blue-background');

    const response = await this.getSearchResults();
    this.spinner.hidden = true;
    this.renderSearchResults(response);
  }

  async getSearchResults() {
    return await (
      await fetch(this.ajaxUrl)
    ).json().catch(() => ({
      searchResults: {
        'vehicles': [],
        'personnel': [],
        'locations': [],
        'others': []
      },
      searchResultCount: 0,
      siteSearchUrl: ''
    }));
  }

  /**
   * Render search results with Mustache.
   *
   * @param  {Object}    object
   * @param  {Object}    searchResults - Categorized search results.
   * @param  {Object}    siteSearchUrl - URL to site search with search term as query param.
   *
   * @return {undefined}
   */

  renderSearchResults({ searchResults = {}, searchResultCount = 0, siteSearchUrl = '' } = {}) {
    if (!this.template) {
      this.template = document.getElementById('site-search-results-template')?.innerHTML || '';
    }

    if (!this.partials) {
      this.partials = {
        vehicle_result: document.getElementById('site-search-vehicle-result-template')?.innerHTML || '',
        other_result: document.getElementById('site-search-other-result-template')?.innerHTML || '',
        personnel_result: document.getElementById('site-search-personnel-result-template')?.innerHTML || '',
        location_result: document.getElementById('site-search-location-result-template')?.innerHTML || ''
      };
    }

    this.resultsWrapper.innerHTML = Mustache.render(this.template, {
      ...searchResults,
      search_result_count: searchResultCount,
      site_search_url: siteSearchUrl
    }, this.partials);
  }

  /**
   * Close search form on outside click.
   *
   * @return {undefined}
   */

  onDocumentClick(target) {
    if (!this.form.hidden && !this.parent.contains(target)) {
      this.closeForm();
    }
  }
}

export default SiteSearch;
